import { Holding } from '../types';

export type AssetClass = 'equities' | 'bonds' | 'commodities' | 'cash';

const SECTOR_MAPPING: Record<string, string> = {
  // Technology
  'AAPL': 'technology', 'MSFT': 'technology', 'NVDA': 'technology', 'AVGO': 'technology', 'ORCL': 'technology', 'CRM': 'technology', 'AMD': 'technology', 'QCOM': 'technology',
  'ADBE': 'technology', 'INTC': 'technology', 'TXN': 'technology', 'MU': 'technology', 'AMAT': 'technology', 'LRCX': 'technology',
  'XLK': 'technology', 'SOXX': 'technology', 'SMH': 'technology',
  
  // Discretionary
  'AMZN': 'discretionary', 'TSLA': 'discretionary', 'HD': 'discretionary', 'MCD': 'discretionary', 'NKE': 'discretionary', 'SBUX': 'discretionary',
  'LOW': 'discretionary', 'BKNG': 'discretionary', 'TJX': 'discretionary', 'ORLY': 'discretionary',
  'XLY': 'discretionary',
  
  // Communication
  'GOOGL': 'communication', 'GOOG': 'communication', 'META': 'communication', 'NFLX': 'communication', 'DIS': 'communication', 'TMUS': 'communication',
  'VZ': 'communication', 'T': 'communication', 'CMCSA': 'communication',
  'XLC': 'communication',
  
  // Financials
  'JPM': 'financials', 'BAC': 'financials', 'WFC': 'financials', 'MS': 'financials', 'GS': 'financials', 'V': 'financials', 'MA': 'financials',
  'AXP': 'financials', 'BLK': 'financials', 'C': 'financials', 'SCHW': 'financials',
  'XLF': 'financials', 'KBE': 'financials', 'KRE': 'financials',
  
  // Energy
  'XOM': 'energy', 'CVX': 'energy', 'COP': 'energy', 'SLB': 'energy', 'EOG': 'energy', 'MPC': 'energy', 'PSX': 'energy',
  'XLE': 'energy', 'XOP': 'energy',
  
  // Industrials
  'CAT': 'industrials', 'GE': 'industrials', 'UNP': 'industrials', 'HON': 'industrials', 'BA': 'industrials',
  'UPS': 'industrials', 'LMT': 'industrials', 'RTX': 'industrials', 'DE': 'industrials',
  'XLI': 'industrials',
  
  // Materials
  'LIN': 'materials', 'APD': 'materials', 'SHW': 'materials', 'ECL': 'materials', 'NEM': 'materials',
  'XLB': 'materials',
  
  // Healthcare
  'LLY': 'healthcare', 'UNH': 'healthcare', 'JNJ': 'healthcare', 'ABBV': 'healthcare', 'MRK': 'healthcare', 'PFE': 'healthcare',
  'TMO': 'healthcare', 'DHR': 'healthcare', 'ISRG': 'healthcare', 'AMGN': 'healthcare',
  'XLV': 'healthcare', 'IBB': 'healthcare', 'XBI': 'healthcare',
  
  // Staples
  'PG': 'staples', 'KO': 'staples', 'PEP': 'staples', 'COST': 'staples', 'WMT': 'staples', 'PM': 'staples', 'EL': 'staples',
  'XLP': 'staples',
  
  // Utilities
  'NEE': 'utilities', 'DUK': 'utilities', 'SO': 'utilities', 'AEP': 'utilities', 'D': 'utilities',
  'XLU': 'utilities',

  // KR Symbols
  '005930': 'technology', '005935': 'technology', '000660': 'technology', '005490': 'materials', '105560': 'financials',
  '055550': 'financials', '005380': 'discretionary', '000270': 'discretionary', '035420': 'communication',
  '035720': 'communication', '207940': 'healthcare', '068270': 'healthcare', '051910': 'materials',
  '373220': 'energy', '006400': 'energy', '010130': 'materials', '036570': 'technology',
  '091160': 'technology', '305710': 'energy', '229200': 'communication', '244580': 'healthcare',
  '091170': 'discretionary', '091180': 'financials',
};

const ASSET_CLASS_MAPPING: Record<string, AssetClass> = {
  // Equities
  'SPY': 'equities', 'QQQ': 'equities', 'DIA': 'equities', 'IWM': 'equities', 'VTI': 'equities',
  'VT': 'equities', 'VEU': 'equities', 'VWO': 'equities', 'EFA': 'equities', 'VOO': 'equities',
  
  // Bonds
  'TLT': 'bonds', 'IEF': 'bonds', 'SHY': 'bonds', 'BND': 'bonds', 'AGG': 'bonds',
  'LQD': 'bonds', 'HYG': 'bonds', 'TIP': 'bonds', 'BNDX': 'bonds', 'MBB': 'bonds',
  
  // Commodities
  'GLD': 'commodities', 'SLV': 'commodities', 'GSG': 'commodities', 'DBC': 'commodities', 'USO': 'commodities',
  'UNG': 'commodities', 'DBA': 'commodities', 'IAU': 'commodities', 'PDBC': 'commodities',
  
  // Cash
  'BIL': 'cash', 'SHV': 'cash', 'CASH': 'cash', 'USD': 'cash', 'USDT': 'cash', 'USDC': 'cash', 'KRW': 'cash',

  // KR ETFs
  '069500': 'equities', '278530': 'equities', '102110': 'equities',
  '122630': 'equities', // KODEX Leveraged
  '114800': 'equities', // KODEX Inverse
  '153130': 'bonds',    // KODEX Short-term Bond
  '273130': 'bonds',    // KODEX 10Y Bond
  '305720': 'commodities', // KODEX Gold
  '132030': 'commodities', // KODEX Gold Futures
};

export function getAssetClass(symbol: string): AssetClass {
  const baseSymbol = symbol.toUpperCase().split('.')[0];
  // If it's in the sector mapping, it's definitely an equity
  if (SECTOR_MAPPING[baseSymbol]) return 'equities';
  return ASSET_CLASS_MAPPING[baseSymbol] || 'equities';
}

export function getSector(symbol: string): string {
  const baseSymbol = symbol.toUpperCase().split('.')[0];
  return SECTOR_MAPPING[baseSymbol] || 'other';
}

export function calculatePortfolioAllocation(holdings: Holding[]) {
  const assetAllocation: Record<AssetClass, number> = {
    equities: 0,
    bonds: 0,
    commodities: 0,
    cash: 0
  };
  
  const sectorAllocation: Record<string, number> = {
    technology: 0,
    discretionary: 0,
    communication: 0,
    financials: 0,
    energy: 0,
    industrials: 0,
    materials: 0,
    healthcare: 0,
    staples: 0,
    utilities: 0,
    other: 0
  };
  
  const totalWeight = holdings.reduce((sum, h) => sum + (h.weight || 0), 0);
  
  if (totalWeight === 0) return { assets: assetAllocation, sectors: sectorAllocation };
  
  holdings.forEach(holding => {
    const assetClass = getAssetClass(holding.symbol);
    const weight = holding.weight || 0;
    
    assetAllocation[assetClass] += weight;
    
    if (assetClass === 'equities') {
      const sector = getSector(holding.symbol);
      sectorAllocation[sector] += weight;
    }
  });
  
  return { assets: assetAllocation, sectors: sectorAllocation };
}
